import React, { useState, useMemo, useRef } from "react";
import DOMPurify from "dompurify";
import usePageMeta from "../hooks/usePageMeta";

/**
 * Time Furniture — public email-signature builder.
 *
 * Anyone on the team can open `/email-signature`, edit the four placeholder
 * fields (Name, Designation, Mobile, Email), preview the rendered signature,
 * and copy either the rendered block (for Gmail / Apple Mail webmail) or the
 * raw HTML (for Outlook / Roundcube source-mode insertion).
 *
 * Signature itself is fully table-based, inline-styled, no scripts, no
 * external CSS — Gmail / Outlook / Roundcube / Apple Mail compatible.
 */

const LOGO_URL = "https://timefurniture.ae/email-signature/time-furniture-logo.png";
// In the preview pane on this page we render with a local URL so the logo
// loads even before the production domain is live. The copied/exported HTML
// always uses the production URL above.
const PREVIEW_LOGO_URL = "/email-signature/time-furniture-logo.png";

// Default content — every field except the four below is constant company info.
const DEFAULTS = {
  name: "Vishnu Vijayan",
  designation: "General Manager",
  mobile: "+971 55 111 0223",
  email: "sales@timefurniture.ae",
};

const COMPANY = {
  legalName: "Time Furniture Turnkey Projects Contracting LLC",
  services:
    "Turnkey Interior Fit-Out | Custom Furniture | Curtains | Upholstery | Joinery | MEP",
  phone: "+971 6 559 1935",
  website: "https://timefurniture.ae",
  websiteLabel: "timefurniture.ae",
  location: "Dubai, UAE",
  footer:
    "UAE since 1993  |  2300+ Projects Delivered  |  Turnkey Specialists",
};

// Brand palette
const C = {
  burgundy: "#6E1F2C",
  gold: "#C8A75D",
  ink: "#1C110C",
  muted: "#7A6B5F",
  divider: "#E5DBC8",
};

/**
 * Build the table-based signature HTML for a given employee.
 * Uses ONLY inline styles + safe email-client HTML.
 */
function buildSignatureHtml({ name, designation, mobile, email }, logoUrl = LOGO_URL) {
  const safe = (s) =>
    String(s || "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;");

  return `<table cellpadding="0" cellspacing="0" border="0" style="font-family: Arial, Helvetica, sans-serif; color: ${C.ink}; font-size: 13px; line-height: 1.55; border-collapse: collapse;">
  <tr>
    <td valign="top" style="padding: 0 22px 0 0;">
      <img src="${logoUrl}" width="120" height="120" alt="Time Furniture" style="display: block; border: 0; outline: none; text-decoration: none; -ms-interpolation-mode: bicubic;" />
    </td>
    <td valign="top" style="border-left: 1px solid ${C.gold}; padding: 0 0 0 22px;">
      <div style="font-family: Arial, Helvetica, sans-serif; font-size: 15px; font-weight: bold; color: ${C.burgundy}; letter-spacing: 0.3px;">${safe(name)}</div>
      <div style="font-family: Arial, Helvetica, sans-serif; font-size: 12px; color: ${C.ink}; margin-top: 2px;">${safe(designation)}</div>
      <div style="font-family: Arial, Helvetica, sans-serif; font-size: 13px; color: ${C.ink}; margin-top: 10px;">${COMPANY.legalName}</div>
      <div style="font-family: Arial, Helvetica, sans-serif; font-size: 11px; color: ${C.muted}; margin-top: 4px; line-height: 1.5;">${COMPANY.services}</div>
      <div style="font-family: Arial, Helvetica, sans-serif; font-size: 12px; color: ${C.ink}; margin-top: 12px; line-height: 1.7;">
        <span style="color: ${C.gold}; font-weight: bold;">T:</span>&nbsp;${COMPANY.phone}&nbsp;&nbsp;<span style="color: ${C.gold}; font-weight: bold;">M:</span>&nbsp;${safe(mobile)}<br />
        <span style="color: ${C.gold}; font-weight: bold;">E:</span>&nbsp;<a href="mailto:${safe(email)}" style="color: ${C.burgundy}; text-decoration: none;">${safe(email)}</a><br />
        <span style="color: ${C.gold}; font-weight: bold;">W:</span>&nbsp;<a href="${COMPANY.website}" style="color: ${C.burgundy}; text-decoration: none;">${COMPANY.websiteLabel}</a><br />
        <span style="color: ${C.gold}; font-weight: bold;">A:</span>&nbsp;${COMPANY.location}
      </div>
      <div style="font-family: Arial, Helvetica, sans-serif; font-size: 10px; color: ${C.muted}; letter-spacing: 1.5px; text-transform: uppercase; margin-top: 14px; padding-top: 10px; border-top: 1px solid ${C.divider};">${COMPANY.footer}</div>
    </td>
  </tr>
</table>`;
}

export default function EmailSignature() {
  usePageMeta({
    title: "Email Signature Builder | Time Furniture",
    description:
      "Internal email-signature builder for Time Furniture team. Edit name, designation, mobile and email — preview, copy and paste into Gmail, Outlook, Roundcube or Apple Mail.",
    canonical: "https://timefurniture.ae/email-signature",
  });

  const [data, setData] = useState(DEFAULTS);
  const [copied, setCopied] = useState(null); // 'rendered' | 'html' | null
  const previewRef = useRef(null);

  const html = useMemo(() => buildSignatureHtml(data), [data]);
  // Preview-pane variant uses a relative URL so the logo loads in the local
  // preview environment even before the production domain is live.
  const previewHtml = useMemo(
    () => buildSignatureHtml(data, PREVIEW_LOGO_URL),
    [data]
  );

  const update = (key) => (e) => setData((d) => ({ ...d, [key]: e.target.value }));

  const reset = () => setData(DEFAULTS);

  const copyHtml = async () => {
    try {
      await navigator.clipboard.writeText(html);
      setCopied("html");
      setTimeout(() => setCopied(null), 2200);
    } catch {
      setCopied("error");
    }
  };

  const copyRendered = async () => {
    if (!previewRef.current) return;
    try {
      const range = document.createRange();
      range.selectNodeContents(previewRef.current);
      const sel = window.getSelection();
      sel.removeAllRanges();
      sel.addRange(range);
      // Try the modern clipboard API with rich content first
      if (window.ClipboardItem) {
        const blobHtml = new Blob([html], { type: "text/html" });
        const blobText = new Blob(
          [previewRef.current.innerText || ""],
          { type: "text/plain" }
        );
        await navigator.clipboard.write([
          new ClipboardItem({
            "text/html": blobHtml,
            "text/plain": blobText,
          }),
        ]);
      } else {
        document.execCommand("copy");
      }
      sel.removeAllRanges();
      setCopied("rendered");
      setTimeout(() => setCopied(null), 2200);
    } catch {
      setCopied("error");
    }
  };

  return (
    <div data-testid="email-signature-page" className="bg-[#FAF5EC] min-h-screen">
      {/* Header strip */}
      <section className="border-b border-[#6E1F2C]/15 bg-white">
        <div className="max-w-[1200px] mx-auto px-6 md:px-12 py-12 md:py-16">
          <p className="text-[10px] tracking-[0.32em] uppercase text-[#6E1F2C] mb-4">
            Internal · Team Resource
          </p>
          <h1
            data-testid="email-signature-h1"
            className="font-serif-display text-3xl md:text-5xl tracking-tight leading-[1.1] text-[#1C110C]"
          >
            Email signature builder.
          </h1>
          <p className="mt-5 max-w-2xl text-[#3E2A22] leading-[1.7] text-base font-light">
            Update the four fields, preview, and copy the signature into Gmail,
            Outlook, Roundcube or Apple Mail. Logo, brand colours and footer
            stay locked.
          </p>
        </div>
      </section>

      <section className="max-w-[1200px] mx-auto px-6 md:px-12 py-12 md:py-16 grid grid-cols-1 lg:grid-cols-12 gap-10">
        {/* Form */}
        <div className="lg:col-span-5">
          <div className="bg-white border border-[#6E1F2C]/15 p-7 md:p-9">
            <p className="text-[10px] tracking-[0.32em] uppercase text-[#6E1F2C] mb-5">
              Step 1 · Edit details
            </p>
            <div className="space-y-5">
              {[
                { k: "name", label: "Full Name" },
                { k: "designation", label: "Designation" },
                { k: "mobile", label: "Mobile" },
                { k: "email", label: "Email" },
              ].map((f) => (
                <div key={f.k}>
                  <label
                    htmlFor={`sig-${f.k}`}
                    className="block text-[10px] tracking-[0.3em] uppercase text-[#7A6B5F] mb-2"
                  >
                    {f.label}
                  </label>
                  <input
                    id={`sig-${f.k}`}
                    data-testid={`sig-input-${f.k}`}
                    type={f.k === "email" ? "email" : "text"}
                    value={data[f.k]}
                    onChange={update(f.k)}
                    className="w-full bg-[#FAF5EC] border border-[#6E1F2C]/20 px-4 py-3 text-[#1C110C] text-base focus:outline-none focus:border-[#6E1F2C] transition-colors"
                  />
                </div>
              ))}
            </div>

            <div className="mt-7 flex flex-col sm:flex-row gap-3">
              <button
                type="button"
                onClick={copyRendered}
                data-testid="sig-copy-rendered"
                className="flex-1 bg-[#6E1F2C] text-[#FAF5EC] px-6 py-4 text-[11px] tracking-[0.28em] uppercase hover:bg-[#56151F] transition-colors"
              >
                {copied === "rendered" ? "Copied" : "Copy Signature"}
              </button>
              <button
                type="button"
                onClick={copyHtml}
                data-testid="sig-copy-html"
                className="flex-1 border border-[#6E1F2C] text-[#6E1F2C] px-6 py-4 text-[11px] tracking-[0.28em] uppercase hover:bg-[#6E1F2C] hover:text-[#FAF5EC] transition-colors"
              >
                {copied === "html" ? "Copied" : "Copy Raw HTML"}
              </button>
            </div>
            <button
              type="button"
              onClick={reset}
              data-testid="sig-reset"
              className="mt-4 text-[10px] tracking-[0.28em] uppercase text-[#7A6B5F] hover:text-[#6E1F2C] transition-colors"
            >
              Reset to default
            </button>
            {copied === "error" && (
              <p className="mt-3 text-xs text-[#A3141A]">
                Couldn&rsquo;t copy automatically. Select the preview block on the
                right and use Ctrl/Cmd-C.
              </p>
            )}
          </div>

          {/* Logo asset card */}
          <div className="mt-8 bg-white border border-[#6E1F2C]/15 p-7 md:p-9">
            <p className="text-[10px] tracking-[0.32em] uppercase text-[#6E1F2C] mb-5">
              Logo Assets
            </p>
            <div className="flex items-center gap-6 flex-wrap">
              <div className="bg-[#FAF5EC] border border-[#6E1F2C]/15 p-4">
                <img
                  src="/email-signature/time-furniture-logo.png"
                  alt="Time Furniture logo on light background"
                  width="180"
                  data-testid="sig-logo-light"
                />
              </div>
              <div className="bg-[#1C110C] p-4">
                <img
                  src="/email-signature/time-furniture-logo-dark.png"
                  alt="Time Furniture logo on dark background"
                  width="180"
                  data-testid="sig-logo-dark"
                />
              </div>
            </div>
            <ul className="mt-6 space-y-2 text-sm text-[#3E2A22] leading-relaxed">
              <li>
                <span className="text-[#7A6B5F]">Light:</span>{" "}
                <a
                  href="/email-signature/time-furniture-logo.png"
                  className="text-[#6E1F2C] underline underline-offset-4 break-all"
                  data-testid="sig-logo-light-url"
                >
                  {LOGO_URL}
                </a>
              </li>
              <li>
                <span className="text-[#7A6B5F]">Dark:</span>{" "}
                <a
                  href="/email-signature/time-furniture-logo-dark.png"
                  className="text-[#6E1F2C] underline underline-offset-4 break-all"
                  data-testid="sig-logo-dark-url"
                >
                  {LOGO_URL.replace(".png", "-dark.png")}
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Preview & Code */}
        <div className="lg:col-span-7">
          <p className="text-[10px] tracking-[0.32em] uppercase text-[#6E1F2C] mb-5">
            Step 2 · Live preview
          </p>
          <div
            className="bg-white border border-[#6E1F2C]/15 p-8 md:p-10 overflow-x-auto"
            data-testid="sig-preview-frame"
          >
            <div
              ref={previewRef}
              data-testid="sig-preview"
              // User-typed fields are sanitized before preview rendering.
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(previewHtml, { ADD_ATTR: ["target"] }),
              }}
            />
          </div>

          <p className="mt-10 text-[10px] tracking-[0.32em] uppercase text-[#6E1F2C] mb-5">
            Step 3 · Raw HTML
          </p>
          <pre
            data-testid="sig-html-source"
            className="bg-[#1C110C] text-[#EDE1C8] p-6 md:p-7 text-xs leading-relaxed overflow-x-auto whitespace-pre-wrap break-all border border-[#6E1F2C]/15"
          >
            {html}
          </pre>
        </div>
      </section>

      {/* Instructions */}
      <section
        data-testid="sig-instructions"
        className="bg-[#F2E9D8] border-y border-[#6E1F2C]/15"
      >
        <div className="max-w-[1200px] mx-auto px-6 md:px-12 py-16 md:py-20">
          <p className="text-[10px] tracking-[0.32em] uppercase text-[#6E1F2C] mb-5">
            How to install
          </p>
          <h2 className="font-serif-display text-2xl md:text-3xl text-[#1C110C] mb-10">
            Step-by-step for each mail client.
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-10">
            <div className="border-t border-[#6E1F2C]/20 pt-6">
              <h3 className="font-serif-display text-xl text-[#1C110C] mb-4">
                Gmail
              </h3>
              <ol className="space-y-2 text-sm text-[#3E2A22] leading-[1.75] list-decimal pl-5">
                <li>Click <em>Copy Signature</em> above.</li>
                <li>
                  Open Gmail → <em>Settings ⚙</em> → <em>See all settings</em> →{" "}
                  <em>General</em>.
                </li>
                <li>
                  Scroll to <em>Signature</em> → <em>Create new</em> → name it
                  &ldquo;Time Furniture&rdquo;.
                </li>
                <li>
                  Click into the signature box and paste (Ctrl/Cmd&nbsp;+&nbsp;V).
                </li>
                <li>
                  Set defaults for new mail and replies → <em>Save Changes</em> at
                  the bottom.
                </li>
              </ol>
            </div>
            <div className="border-t border-[#6E1F2C]/20 pt-6">
              <h3 className="font-serif-display text-xl text-[#1C110C] mb-4">
                Outlook
              </h3>
              <ol className="space-y-2 text-sm text-[#3E2A22] leading-[1.75] list-decimal pl-5">
                <li>Click <em>Copy Raw HTML</em> above.</li>
                <li>
                  Outlook desktop: <em>File</em> → <em>Options</em> →{" "}
                  <em>Mail</em> → <em>Signatures</em>.
                </li>
                <li>
                  Create a new signature named &ldquo;Time Furniture&rdquo;.
                </li>
                <li>
                  Paste the rendered signature directly (Ctrl&nbsp;+&nbsp;V) — or for
                  exact fidelity, edit the HTML signature file in{" "}
                  <code>%appdata%\Microsoft\Signatures\</code>.
                </li>
                <li>
                  Set as default for new emails and replies → <em>OK</em>.
                </li>
              </ol>
            </div>
            <div className="border-t border-[#6E1F2C]/20 pt-6">
              <h3 className="font-serif-display text-xl text-[#1C110C] mb-4">
                Apple Mail / Webmail / Roundcube
              </h3>
              <ol className="space-y-2 text-sm text-[#3E2A22] leading-[1.75] list-decimal pl-5">
                <li>Click <em>Copy Signature</em> above.</li>
                <li>
                  Apple Mail: <em>Mail</em> → <em>Settings</em> →{" "}
                  <em>Signatures</em>. Create new, paste, uncheck &ldquo;Always
                  match my default message font&rdquo;.
                </li>
                <li>
                  Roundcube / cPanel webmail: <em>Settings</em> →{" "}
                  <em>Identities</em> → toggle <em>HTML signature</em>{" "}
                  on, paste, save.
                </li>
                <li>
                  Send a test email to yourself — verify logo loads from{" "}
                  <code>timefurniture.ae</code>.
                </li>
              </ol>
            </div>
          </div>
          <p className="mt-12 text-xs text-[#7A6B5F] italic max-w-2xl">
            Tip: if the logo doesn&rsquo;t appear in a recipient&rsquo;s inbox, ask
            them to mark Time Furniture as a trusted sender — most mail clients
            block remote images by default until trust is established.
          </p>
        </div>
      </section>
    </div>
  );
}
